// frontend/app/components/discoveryJourneys/journeyProgress.js
//
// The small amount of derivation the three Discovery Journey components share,
// kept out of them so a loop's state means the same thing on the launcher card
// and on the map.
//
// Nothing here knows the name of a journey or a loop. Everything is read off
// the payload the API returned.

/** Every state a loop can be in, ordered as the student meets them. */
export const LOOP_STATES = ["not_started", "in_progress", "complete", "unavailable"];

const KNOWN_STATE = new Set(LOOP_STATES);

/**
 * Interpolates `{placeholders}` into an already-translated sentence, so a
 * translator receives one whole clause instead of fragments that only
 * reassemble in English word order. Same convention as the lessonPlans
 * components.
 */
export const fill = (template, values) =>
  String(template).replace(/\{(\w+)\}/g, (token, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : token
  );

/**
 * The loop's state, preferring what the server said.
 *
 * `unavailable` is decided first and on three signals — an explicit status, an
 * explicit `available: false`, or a missing `lesson_plan_id` — because a loop
 * whose lesson could not be resolved must never be offered as openable. The
 * rest of the chain still renders around it (design § Error handling).
 */
export const loopStateOf = (loop) => {
  if (!loop) return "unavailable";

  const declared = String(loop.status || "").toLowerCase();
  if (declared === "unavailable" || loop.available === false) return "unavailable";
  if (loop.lesson_plan_id === null || loop.lesson_plan_id === undefined) {
    return "unavailable";
  }
  if (KNOWN_STATE.has(declared)) return declared;

  const progress = loop.progress || null;
  if (progress?.completed) return "complete";
  if (progress && (Number(progress.step_index) > 0 || Number(progress.visited_through_index) > 0)) {
    return "in_progress";
  }
  return progress ? "in_progress" : "not_started";
};

/** Why a loop is unavailable, in the server's words. Never invented here. */
export const unavailableReason = (loop) =>
  String(loop?.reason || loop?.unavailable_reason || "").trim();

const lessonOf = (loop) => loop?.lesson || loop?.lesson_plan || null;

/** Duration and step count live on the member lesson, or flattened onto the loop. */
export const loopLessonMeta = (loop) => {
  const lesson = lessonOf(loop);
  const pick = (key) => {
    const value = lesson?.[key] ?? loop?.[key];
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  };
  return {
    title: lesson?.title || loop?.lesson_title || "",
    durationMinutes: pick("duration_minutes"),
    stepCount: pick("step_count"),
  };
};

/**
 * How far into a loop the student is, as `{ step, total }` — or null when there
 * is nothing honest to say. `step_index` is 0-based; a finished loop reports its
 * last step, so completion is read from `completed`, never from the index.
 */
export const loopStepPosition = (loop) => {
  const progress = loop?.progress || null;
  const { stepCount } = loopLessonMeta(loop);
  if (!progress || !stepCount) return null;
  if (progress.completed) return { step: stepCount, total: stepCount };
  const index = Number(progress.step_index) || 0;
  return { step: Math.min(index + 1, stepCount), total: stepCount };
};

/**
 * Journey-level progress across the chain.
 *
 * `current` is 1-based and points at the first loop still to do, which is what
 * "Loop 2 of 5" means to a student. Unavailable loops still count toward
 * `total` — hiding them would make the chain lie about its own length — but
 * they can never be the one the student is asked to open next.
 */
export const journeyProgress = (loops = []) => {
  const list = Array.isArray(loops) ? loops : [];
  const states = list.map(loopStateOf);
  const completed = states.filter((state) => state === "complete").length;
  const unavailable = states.filter((state) => state === "unavailable").length;

  const inProgressAt = states.indexOf("in_progress");
  const nextAt = states.findIndex(
    (state) => state === "not_started" || state === "in_progress"
  );
  const at = inProgressAt >= 0 ? inProgressAt : nextAt;

  return {
    total: list.length,
    completed,
    unavailable,
    // Everything done (or nothing left that can be done) has no "current".
    current: at >= 0 ? at + 1 : null,
    isComplete: list.length > 0 && completed + unavailable === list.length,
  };
};

/**
 * The same summary from a launcher row, which carries counts rather than the
 * loops themselves. A `progressByJourney` entry wins over anything embedded in
 * the row, so a caller that fetched progress separately is authoritative.
 */
export const journeyRowProgress = (journey, progress) => {
  const source = progress || journey?.progress || null;
  const total =
    Number(journey?.loop_count) ||
    Number(source?.loop_count) ||
    (Array.isArray(journey?.loops) ? journey.loops.length : 0);

  if (!source) return { total, completed: 0, current: null, isComplete: false };

  const completed = Math.min(
    Number(source.completed_loops) || 0,
    total || Number(source.completed_loops) || 0
  );
  const isComplete = Boolean(source.completed) || (total > 0 && completed >= total);
  const index = Number(source.loop_index) || 0;

  return {
    total,
    completed,
    current: isComplete ? null : Math.min(index + 1, total || index + 1),
    isComplete,
  };
};
